import { type ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  NeonButton,
  NeonCheckboxField,
  NeonCheckboxSingle,
  NeonIcon,
  NeonNumberField,
  NeonSelectField,
  NeonTag,
  NeonTextField,
} from '@ps-refarch-ux/neon';
import type { CheckboxOption } from '@ps-refarch-ux/neon';
import {
  getAddCourseBreadcrumbs,
  clearCourseCatalogFilterSession,
  ADD_COURSE_PAGE_TEXT,
  VIEW_COURSE_PAGE_TEXT,
  COURSE_CATALOG_ID,
  COURSE_DURATION_OPTION_CLEAR,
  COURSE_DURATION_SELECT_OPTIONS,
  COURSE_STATUS_OPTIONS,
  GRADE_LEVEL_CHECKBOX_OPTIONS,
  INSTRUCTIONAL_LEVEL_OPTION_CLEAR,
  INSTRUCTIONAL_LEVEL_SELECT_OPTIONS,
  MAX_COURSE_DESCRIPTION_PLAIN_TEXT_LENGTH,
  MAX_PREREQUISITE_DESCRIPTION_LENGTH,
  formatCourseAddedToCatalogToastHeading,
} from '@/constants/course-catalog-constants';
import { STAFF_ROUTES } from '@/constants/staff-routes';
import { NeonButtonTypes } from '@/constants/neon-constants';
import { useGetSubjectAreas, useCreateSubjectArea } from '@/repositories/subject-areas-repository';
import { SCEDSubjectCodes } from '@/constants/subject-area-constants';
import {
  formatSubjectAreaOptionLabel,
  isDuplicateSubjectArea,
  subjectAreaRefFromSelectValue,
  subjectAreaToOptionValue,
} from '@/utils/subject-area-utils';
import { AddSubjectAreaDialog } from '@/containers/SubjectAreas/add-subject-area-dialog';
import { CourseSchoolsMappingDrawer } from './course-schools-mapping-drawer';
import {
  AddCourseCatalogMultiSelect,
  type CourseCatalogMultiSelectItem,
} from './add-course-catalog-multi-select';
import {
  useCourseIdExists,
  useCreateCourse,
  useGetCourse,
  useUpdateCourse,
} from '@/repositories/course-repository';
import { useDebouncedValue } from '@/hooks/use-debounce';
import { useGetSchools } from '@/repositories/schools-repository';
import { useToastNotifications } from '@/hooks/use-toast-notifications';
import type { Course, CourseDetail, CourseStatus, SubjectAreaRef } from '@/types';
import { FormPageShell } from '@/components/FormPageShell/form-page-shell';
import { MultiSectionFormLayout } from '@/components/MultiSectionFormLayout/multi-section-form-layout';
import { DescriptionField } from '@/components/DescriptionField/description-field';
import { isCreditRecoveryCoursesFeatureEnabled, isCourseCatalogReadOnly } from '@/utils/cp-context';
import { htmlToPlainText } from '@/utils/sanitize-rich-html';
import styles from './add-course-page.module.scss';
import { DEFAULT_CREDITS_INPUT_MAX_DECIMALS } from '@/utils/sanitize-credits-input';

/** District catalog import placeholder school NID — omit from mapped-schools display (same as view course). */
const DUS_NID_PATTERN = /^\d+DUS$/i;

/** Legacy CourseForm strips course ID to alphanumerics plus _ - . / and uppercases. */
function normalizeCourseIdInput(raw: string): string {
  const matches = raw.match(/[a-zA-Z0-9]*[a-zA-Z0-9_\-.\\/]*[a-zA-Z0-9]*/g);
  return (matches ? matches.join('') : '').toUpperCase();
}

function stripNewlines(s: string): string {
  return s.replace(/(?:\r\n|\r|\n)/g, '');
}

function parseCreditsValue(raw: number | string | undefined | null): number | undefined {
  if (raw === undefined || raw === null) {
    return undefined;
  }
  if (typeof raw === 'number') {
    return Number.isFinite(raw) ? raw : undefined;
  }
  const t = raw.trim();
  if (!t) {
    return undefined;
  }
  const n = parseFloat(t);
  return Number.isFinite(n) ? n : undefined;
}

/** Match legacy CourseForm description handling (HTML from RTE, no raw newlines in stored string). */
function normalizeDescriptionForSave(html: string): string | undefined {
  const stripped = html.replace(/(?:\r\n|\r|\n)/g, '').trim();
  if (!stripped) {
    return undefined;
  }
  return stripped;
}

interface CourseSelectionItem {
  id: string;
  name: string;
}

interface CourseFormState {
  courseName: string;
  courseId: string;
  stateId: string;
  subjectArea: string;
  credits: number | undefined;
  courseDuration: string;
  gradeLevels: Record<string, boolean>;
  status: string;
  courseDescription: string;
  instructionalLevel: string;
  creditType: string;
  canBeRepeatedForCredit: boolean;
  qualifiesAsCTE: boolean;
  requiresTeacherRecommendation: boolean;
  prerequisiteDescription: string;
  creditRecoverySelected: CourseSelectionItem[];
  equivalentSelected: CourseSelectionItem[];
  /** Each inner array is one AND group; OR between groups (matches API `coursePrerequisites`). */
  prerequisiteGroups: CourseSelectionItem[][];
  corequisiteSelected: CourseSelectionItem[];
}

interface AddSubjectAreaModalState {
  isOpen: boolean;
  subjectAreaName: string;
  selectedScedCode?: string;
}

const INITIAL_COURSE_FORM_STATE: CourseFormState = {
  courseName: '',
  courseId: '',
  stateId: '',
  subjectArea: '',
  credits: undefined,
  courseDuration: '',
  gradeLevels: {},
  status: COURSE_STATUS_OPTIONS[0].value,
  courseDescription: '',
  instructionalLevel: '',
  creditType: '',
  canBeRepeatedForCredit: false,
  qualifiesAsCTE: false,
  requiresTeacherRecommendation: false,
  prerequisiteDescription: '',
  creditRecoverySelected: [],
  equivalentSelected: [],
  prerequisiteGroups: [[]],
  corequisiteSelected: [],
};

const INITIAL_ADD_SUBJECT_AREA_MODAL_STATE: AddSubjectAreaModalState = {
  isOpen: false,
  subjectAreaName: '',
  selectedScedCode: undefined,
};

function toCourseSelectionArray(
  input?: Array<string[] | Course[] | { id: string; name?: string }[]>
): Array<{ id: string; name: string }> {
  if (!input || input.length === 0) {
    return [];
  }

  const normalized = new Map<string, { id: string; name: string }>();

  for (const group of input) {
    if (!Array.isArray(group)) {
      continue;
    }

    for (const item of group) {
      if (typeof item === 'string') {
        normalized.set(item, { id: item, name: item });
        continue;
      }

      const id = typeof item?.id === 'string' ? item.id : '';
      if (!id) {
        continue;
      }
      const name = typeof item?.name === 'string' && item.name.trim().length > 0 ? item.name : id;
      normalized.set(id, { id, name });
    }
  }

  return Array.from(normalized.values());
}

/**
 * Maps API `coursePrerequisites` into form state (same shape as view `toRelationshipGroups`).
 * Returns one empty row when there are no groups.
 */
function toPrerequisiteGroupsFromCourse(
  input?: CourseDetail['coursePrerequisites']
): CourseSelectionItem[][] {
  if (!input?.length) {
    return [[]];
  }
  const groups = input
    .map((group) => {
      if (!Array.isArray(group)) {
        return [];
      }
      return group
        .map((item) => {
          if (typeof item === 'string') {
            return item ? { id: item, name: item } : null;
          }
          const id = typeof item?.id === 'string' ? item.id : '';
          if (!id) {
            return null;
          }
          const name =
            typeof item?.name === 'string' && item.name.trim().length > 0 ? item.name : id;
          return { id, name };
        })
        .filter((x): x is CourseSelectionItem => x !== null);
    })
    .filter((g) => g.length > 0);
  return groups.length > 0 ? groups : [[]];
}

function prerequisiteGroupsToPayload(groups: CourseSelectionItem[][]): string[][] {
  return groups.map((g) => g.map((c) => c.id)).filter((ids) => ids.length > 0);
}

/**
 * Credit recovery and equivalent courses are persisted from the first OR-group only
 * (`CourseConverter.extractCourseAnnotations` uses `cleanUpStatements(...)[0]`; `getEquivalentCourseIds`
 * reads `equivalentCourses?.[0]`). Legacy CPUI sends every selected id in one inner array
 * `[["A","B","C"]]`. Sending one id per group `[["A"],["B"]]` causes only the first id to save.
 */
function creditRecoveryAndEquivalentsToApiPayload(
  creditRecoverySelected: CourseSelectionItem[],
  equivalentSelected: CourseSelectionItem[]
): { courseCreditRecovery: string[][]; equivalentCourses: string[][] } {
  const crIds = creditRecoverySelected.map((c) => c.id).filter(Boolean);
  const eqIds = equivalentSelected.map((c) => c.id).filter(Boolean);
  return {
    courseCreditRecovery: crIds.length ? [crIds] : [],
    equivalentCourses: eqIds.length ? [eqIds] : [],
  };
}

/** True when every grade checkbox (6–12) is on; drives “Select all” staying in sync. */
function areAllIndividualGradeLevelsSelected(gradeLevels: Record<string, boolean>): boolean {
  return GRADE_LEVEL_CHECKBOX_OPTIONS.filter((opt) => opt.value !== 'select-all').every(
    (opt) => gradeLevels[opt.value] === true
  );
}

function hasAtLeastOneGradeLevelSelected(gradeLevels: Record<string, boolean>): boolean {
  return GRADE_LEVEL_CHECKBOX_OPTIONS.some(
    (opt) => opt.value !== 'select-all' && Boolean(gradeLevels[opt.value])
  );
}

function gradeLevelsFromSelection(
  selectedValues: string[] | undefined,
  previousGradeLevels: Record<string, boolean>
): Record<string, boolean> {
  const selected = new Set(selectedValues ?? []);
  const nextGradeLevels: Record<string, boolean> = {};
  const gradeValues = GRADE_LEVEL_CHECKBOX_OPTIONS.filter((opt) => opt.value !== 'select-all').map(
    (opt) => opt.value
  );

  const selectAllChosen = selected.has('select-all');
  const allIndividualChosen = gradeValues.every((value) => selected.has(value));
  const wasSelectAllEnabled = Boolean(previousGradeLevels['select-all']);

  // Neon may emit all individual values when the user unchecks only "Select all".
  // Treat that transition as "clear all" so the checkbox can be deselected predictably.
  if (wasSelectAllEnabled && !selectAllChosen && allIndividualChosen) {
    gradeValues.forEach((value) => {
      nextGradeLevels[value] = false;
    });
    nextGradeLevels['select-all'] = false;
    return nextGradeLevels;
  }

  // If select-all was just turned on, force all grades on.
  // If it was already on, keep it only while all individual grades remain selected.
  const applyToAll =
    (selectAllChosen && !wasSelectAllEnabled) ||
    (selectAllChosen && wasSelectAllEnabled && allIndividualChosen);

  gradeValues.forEach((value) => {
    nextGradeLevels[value] = applyToAll ? true : selected.has(value);
  });

  nextGradeLevels['select-all'] = areAllIndividualGradeLevelsSelected(nextGradeLevels);
  return nextGradeLevels;
}

function buildFormStateFromCourse(course: CourseDetail): CourseFormState {
  const gradeMap: Record<string, boolean> = {};
  (course.grades ?? []).forEach((grade) => {
    gradeMap[String(grade)] = true;
  });
  if (areAllIndividualGradeLevelsSelected(gradeMap)) {
    gradeMap['select-all'] = true;
  }

  return {
    ...INITIAL_COURSE_FORM_STATE,
    courseName: course.name ?? '',
    courseId: course.id ?? '',
    stateId: course.stateId ?? '',
    subjectArea: course.subjectArea
      ? subjectAreaToOptionValue({
          name: course.subjectArea.name,
          CSSCCourse: course.subjectArea.CSSCCourse ?? 0,
          SCEDSubject: course.subjectArea.SCEDSubject ?? 0,
        })
      : '',
    credits: Number.isFinite(course.credits) ? course.credits : undefined,
    courseDuration: course.courseDuration?.trim() ?? '',
    status: course.status ?? COURSE_STATUS_OPTIONS[0].value,
    courseDescription: course.description ?? '',
    instructionalLevel: course.instructionalLevel ?? '',
    creditType: course.creditType ?? '',
    canBeRepeatedForCredit: Boolean(course.repeatable),
    qualifiesAsCTE: Boolean(course.cteCourse),
    requiresTeacherRecommendation: Boolean(course.permissionRequired),
    prerequisiteDescription: course.prerequisites ?? '',
    gradeLevels: gradeMap,
    creditRecoverySelected: toCourseSelectionArray(course.courseCreditRecovery),
    equivalentSelected: toCourseSelectionArray(course.equivalentCourses),
    prerequisiteGroups: toPrerequisiteGroupsFromCourse(course.coursePrerequisites),
    corequisiteSelected: toCourseSelectionArray(course.courseCorequisites),
  };
}

export function AddCoursePage(): ReactElement {
  const navigate = useNavigate();
  const { id: routeCourseId } = useParams<{ id: string }>();
  const editCourseId = routeCourseId ? decodeURIComponent(routeCourseId) : '';
  const isEditMode = editCourseId.length > 0;
  const isDistrictLevel = !isCourseCatalogReadOnly();
  const { showToast, closeToastById } = useToastNotifications();
  const [formState, setFormState] = useState<CourseFormState>(INITIAL_COURSE_FORM_STATE);
  const [addSubjectAreaModalState, setAddSubjectAreaModalState] =
    useState<AddSubjectAreaModalState>(INITIAL_ADD_SUBJECT_AREA_MODAL_STATE);
  const {
    courseName,
    courseId,
    stateId,
    subjectArea,
    credits,
    courseDuration,
    gradeLevels,
    status,
    courseDescription,
    instructionalLevel,
    creditType,
    canBeRepeatedForCredit,
    qualifiesAsCTE,
    requiresTeacherRecommendation,
    prerequisiteDescription,
    creditRecoverySelected,
    equivalentSelected,
    prerequisiteGroups,
    corequisiteSelected,
  } = formState;

  const {
    isOpen: showAddSubjectAreaModal,
    subjectAreaName: newSubjectAreaName,
    selectedScedCode: newSelectedScedCode,
  } = addSubjectAreaModalState;

  const { data: subjectAreasData } = useGetSubjectAreas();
  const createSubjectArea = useCreateSubjectArea();
  const {
    data: courseToEdit,
    isLoading: isCourseToEditLoading,
    refetch: refetchCourseToEdit,
  } = useGetCourse(editCourseId, isEditMode);
  const createCourseMutation = useCreateCourse();
  const updateCourseMutation = useUpdateCourse();
  const { data: schoolsData, isLoading: isSchoolsLoading } = useGetSchools();
  const [courseToMap, setCourseToMap] = useState<{
    id: string;
    name: string;
    schools: string[];
  } | null>(null);
  /**
   * NeonSelectField syncs modelValue in an effect keyed only by the prop. When the user picks
   * "None" from an already-empty field, modelValue stays `undefined` so the web component never
   * clears its internal sentinel — remount forces the placeholder to show again.
   */
  const [instructionalLevelSelectRemountKey, setInstructionalLevelSelectRemountKey] = useState(0);
  const [courseDurationSelectRemountKey, setCourseDurationSelectRemountKey] = useState(0);

  /** Same 1s debounce as legacy c-p-ui `CourseIdField` / `Debounce`. */
  const courseIdTrimmed = courseId.trim();
  const debouncedCourseId = useDebouncedValue(courseIdTrimmed, 1000);
  const courseIdExistsQuery = useCourseIdExists(debouncedCourseId, { enabled: !isEditMode });
  const idsMatchForExistsCheck = debouncedCourseId === courseIdTrimmed;
  const courseIdInUse =
    !isEditMode &&
    idsMatchForExistsCheck &&
    courseIdExistsQuery.data === true &&
    !courseIdExistsQuery.isFetching;
  const checkingCourseId =
    !isEditMode &&
    courseIdTrimmed.length > 0 &&
    idsMatchForExistsCheck &&
    courseIdExistsQuery.isFetching;

  /** Helper for neutral/async states only. "Already in use" is shown separately — Neon hides non-required errors until blur. */
  const courseIdHelperText = useMemo(() => {
    if (isEditMode) {
      return undefined;
    }
    if (checkingCourseId) {
      return ADD_COURSE_PAGE_TEXT.CourseIdChecking;
    }
    if (courseIdExistsQuery.isError && idsMatchForExistsCheck && courseIdTrimmed.length > 0) {
      return ADD_COURSE_PAGE_TEXT.CourseIdVerifyError;
    }
    return undefined;
  }, [
    isEditMode,
    checkingCourseId,
    courseIdExistsQuery.isError,
    idsMatchForExistsCheck,
    courseIdTrimmed.length,
  ]);

  const descriptionPlainTextExceedsMax = useMemo(() => {
    const plain = htmlToPlainText(courseDescription);
    return plain.length > MAX_COURSE_DESCRIPTION_PLAIN_TEXT_LENGTH;
  }, [courseDescription]);

  const prerequisiteTextExceedsMax =
    (prerequisiteDescription?.length ?? 0) > MAX_PREREQUISITE_DESCRIPTION_LENGTH;

  /** Save / Save & Map only when required overview fields are valid and (add) course ID is confirmed available. */
  const canSaveCourse = useMemo(() => {
    if (descriptionPlainTextExceedsMax || prerequisiteTextExceedsMax) {
      return false;
    }
    if (courseName.trim().length === 0) {
      return false;
    }
    if (courseId.trim().length === 0) {
      return false;
    }
    if (subjectArea.trim().length === 0) {
      return false;
    }
    if (parseCreditsValue(credits) === undefined) {
      return false;
    }
    if (!hasAtLeastOneGradeLevelSelected(gradeLevels)) {
      return false;
    }
    if (isEditMode) {
      return true;
    }
    return (
      courseIdTrimmed.length > 0 &&
      idsMatchForExistsCheck &&
      !courseIdExistsQuery.isFetching &&
      !courseIdExistsQuery.isError &&
      courseIdExistsQuery.data === false
    );
  }, [
    courseName,
    courseId,
    subjectArea,
    credits,
    gradeLevels,
    isEditMode,
    courseIdTrimmed,
    idsMatchForExistsCheck,
    courseIdExistsQuery.isFetching,
    courseIdExistsQuery.isError,
    courseIdExistsQuery.data,
    descriptionPlainTextExceedsMax,
    prerequisiteTextExceedsMax,
  ]);

  const updateFormState = useCallback((updates: Partial<CourseFormState>) => {
    setFormState((prev) => ({ ...prev, ...updates }));
  }, []);

  // School users are read-only — redirect them away from add/edit pages.
  useEffect(() => {
    if (!isDistrictLevel) {
      navigate(`/${STAFF_ROUTES.courseCatalog.list}`, { replace: true });
    }
  }, [isDistrictLevel, navigate]);

  useEffect(() => {
    if (!isEditMode || !courseToEdit) {
      return;
    }
    setFormState(buildFormStateFromCourse(courseToEdit));
  }, [isEditMode, courseToEdit]);

  const subjectAreaList = useMemo(() => subjectAreasData?.data ?? [], [subjectAreasData?.data]);

  const filteredEditSchoolNids = useMemo(() => {
    if (!isEditMode || !courseToEdit?.schools?.length) {
      return [];
    }
    return courseToEdit.schools.filter((schoolNid) => !DUS_NID_PATTERN.test(schoolNid));
  }, [isEditMode, courseToEdit?.schools]);

  const mappedEditSchoolsDisplay = useMemo(() => {
    if (!filteredEditSchoolNids.length || !schoolsData?.length) {
      return [];
    }
    const byNid = new Map(schoolsData.map((s) => [s.nid, s.name]));
    return filteredEditSchoolNids
      .map((schoolNid) => ({ nid: schoolNid, label: byNid.get(schoolNid) ?? schoolNid }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [filteredEditSchoolNids, schoolsData]);

  const subjectAreaOptions = useMemo(() => {
    const fromList = subjectAreaList.map((sa) => ({
      text: formatSubjectAreaOptionLabel(sa),
      value: subjectAreaToOptionValue(sa),
    }));
    const valueSet = new Set(fromList.map((o) => o.value));
    if (isEditMode && courseToEdit?.subjectArea && subjectArea && !valueSet.has(subjectArea)) {
      fromList.push({
        text: formatSubjectAreaOptionLabel({
          name: courseToEdit.subjectArea.name,
          CSSCCourse: courseToEdit.subjectArea.CSSCCourse ?? 0,
          SCEDSubject: courseToEdit.subjectArea.SCEDSubject ?? 0,
        }),
        value: subjectArea,
      });
    }
    fromList.sort((a, b) => a.text.localeCompare(b.text, undefined, { sensitivity: 'base' }));
    return fromList;
  }, [subjectAreaList, isEditMode, courseToEdit, subjectArea]);

  const instructionalLevelOptions = useMemo(() => {
    const current = instructionalLevel.trim();
    const known = new Set(
      INSTRUCTIONAL_LEVEL_SELECT_OPTIONS.map((o) => o.value).filter(
        (v) => v !== INSTRUCTIONAL_LEVEL_OPTION_CLEAR
      )
    );
    if (current && !known.has(current)) {
      return [...INSTRUCTIONAL_LEVEL_SELECT_OPTIONS, { value: current, text: current }];
    }
    return [...INSTRUCTIONAL_LEVEL_SELECT_OPTIONS];
  }, [instructionalLevel]);

  const courseDurationOptions = useMemo(() => {
    const current = courseDuration.trim();
    const known = new Set(
      COURSE_DURATION_SELECT_OPTIONS.map((o) => o.value).filter(
        (v) => v !== COURSE_DURATION_OPTION_CLEAR
      )
    );
    if (current && !known.has(current)) {
      return [...COURSE_DURATION_SELECT_OPTIONS, { value: current, text: current }];
    }
    return [...COURSE_DURATION_SELECT_OPTIONS];
  }, [courseDuration]);

  const statusOptions = COURSE_STATUS_OPTIONS.map((o) => ({ text: o.text, value: o.value }));

  const gradeLevelCheckboxOptions = useMemo<CheckboxOption[]>(
    () =>
      GRADE_LEVEL_CHECKBOX_OPTIONS.map((opt) => ({
        id: `add-course-grade-${opt.value}`,
        labelText: opt.label,
        name: opt.value,
      })),
    []
  );

  const selectedGradeLevelValues = useMemo(
    () =>
      GRADE_LEVEL_CHECKBOX_OPTIONS.map((opt) => opt.value).filter((value) =>
        Boolean(gradeLevels[value])
      ),
    [gradeLevels]
  );

  const showCreditRecoverySection = isCreditRecoveryCoursesFeatureEnabled();

  const onCreditRecoverySelectionChange = useCallback(
    (next: CourseCatalogMultiSelectItem[]) => {
      updateFormState({ creditRecoverySelected: next });
    },
    [updateFormState]
  );

  const onEquivalentSelectionChange = useCallback(
    (next: CourseCatalogMultiSelectItem[]) => {
      updateFormState({ equivalentSelected: next });
    },
    [updateFormState]
  );

  const onCorequisiteSelectionChange = useCallback(
    (next: CourseCatalogMultiSelectItem[]) => {
      updateFormState({ corequisiteSelected: next });
    },
    [updateFormState]
  );

  const onPrerequisiteGroupChange = useCallback(
    (groupIndex: number, next: CourseCatalogMultiSelectItem[]) => {
      setFormState((prev) => ({
        ...prev,
        prerequisiteGroups: prev.prerequisiteGroups.map((g, i) => (i === groupIndex ? next : g)),
      }));
    },
    []
  );

  const addPrerequisiteOrGroup = useCallback(() => {
    setFormState((prev) => ({
      ...prev,
      prerequisiteGroups: [...prev.prerequisiteGroups, []],
    }));
  }, []);

  const removePrerequisiteOrGroup = useCallback((groupIndex: number) => {
    if (groupIndex <= 0) {
      return;
    }
    setFormState((prev) => {
      const next = prev.prerequisiteGroups.filter((_, i) => i !== groupIndex);
      return {
        ...prev,
        prerequisiteGroups: next.length > 0 ? next : [[]],
      };
    });
  }, []);

  const handleCancel = useCallback(() => {
    navigate(`/${STAFF_ROUTES.courseCatalog.list}`);
  }, [navigate]);

  const handleSave = useCallback(() => {
    if (descriptionPlainTextExceedsMax || prerequisiteTextExceedsMax) {
      return;
    }
    if (!canSaveCourse) {
      return;
    }
    if (!isEditMode && courseIdInUse) {
      return;
    }
    const creditsNum = parseCreditsValue(credits);
    const gradesArray = GRADE_LEVEL_CHECKBOX_OPTIONS.filter(
      (opt) => opt.value !== 'select-all' && gradeLevels[opt.value]
    ).map((opt) => parseInt(opt.value, 10));

    const subjectAreaPayload: SubjectAreaRef | undefined = subjectArea
      ? subjectAreaRefFromSelectValue(subjectArea, subjectAreaList)
      : undefined;

    const payload: { course: Partial<CourseDetail> } = {
      course: {
        name: courseName.trim(),
        id: courseId.trim(),
        stateId: stateId.trim() || undefined,
        subjectArea: subjectAreaPayload,
        credits: creditsNum,
        grades: gradesArray,
        status: status as CourseStatus,
        /* Edit: preserve mapped schools from GET (was hardcoded [] and wiped annotations on save). */
        schools: isEditMode ? (courseToEdit?.schools ?? []) : [],
        description: normalizeDescriptionForSave(courseDescription),
        instructionalLevel: instructionalLevel.trim() || undefined,
        creditType: creditType.trim() || undefined,
        courseDuration: courseDuration.trim() || undefined,
        repeatable: canBeRepeatedForCredit,
        cteCourse: qualifiesAsCTE,
        permissionRequired: requiresTeacherRecommendation,
        prerequisites: (() => {
          const t = stripNewlines(prerequisiteDescription).trim();
          return t || undefined;
        })(),
        stateCore: false,
        elective: false,
        techPrepCourse: false,
        coursePrerequisites: prerequisiteGroupsToPayload(prerequisiteGroups),
        courseCorequisites: corequisiteSelected.length
          ? [corequisiteSelected.map((c) => c.id)]
          : [],
        ...creditRecoveryAndEquivalentsToApiPayload(creditRecoverySelected, equivalentSelected),
        studentGroupRestrictions: [],
      },
    };

    if (isEditMode) {
      updateCourseMutation.mutate(payload, {
        onSuccess: () => {
          showToast('success', { heading: 'The course was successfully updated.' });
          navigate(`/${STAFF_ROUTES.courseCatalog.list}`);
        },
        onError: () => {
          showToast('error', { heading: 'Unable to update course. Please try again.' });
        },
      });
      return;
    }

    createCourseMutation.mutate(payload, {
      onSuccess: () => {
        const name = courseName.trim();
        const id = courseId.trim();
        const toastIdPromise = showToast('success', {
          heading: formatCourseAddedToCatalogToastHeading(name),
          buttons: [
            {
              id: 'add-course-toast-map-schools',
              text: ADD_COURSE_PAGE_TEXT.ToastMapToSchools,
              type: 'secondary',
              onClick: () => {
                const goToMapping = () => {
                  void navigate(`/${STAFF_ROUTES.courseCatalog.list}`, {
                    state: { openMappingForCourse: { id, name } },
                  });
                };
                if (toastIdPromise) {
                  void toastIdPromise.then((toastId) => {
                    if (toastId) {
                      closeToastById(toastId);
                    }
                    goToMapping();
                  });
                } else {
                  goToMapping();
                }
              },
            },
          ],
        });
        navigate(`/${STAFF_ROUTES.courseCatalog.list}`);
      },
      onError: () => {
        showToast('error', { heading: 'Unable to create course. Please try again.' });
      },
    });
  }, [
    courseName,
    courseId,
    stateId,
    subjectArea,
    subjectAreaList,
    credits,
    courseDuration,
    gradeLevels,
    status,
    courseDescription,
    instructionalLevel,
    creditType,
    canBeRepeatedForCredit,
    qualifiesAsCTE,
    requiresTeacherRecommendation,
    prerequisiteDescription,
    creditRecoverySelected,
    equivalentSelected,
    prerequisiteGroups,
    corequisiteSelected,
    isEditMode,
    courseToEdit,
    createCourseMutation,
    updateCourseMutation,
    showToast,
    closeToastById,
    navigate,
    courseIdInUse,
    canSaveCourse,
    descriptionPlainTextExceedsMax,
    prerequisiteTextExceedsMax,
  ]);

  const handleOpenMappingDrawerForEdit = useCallback(() => {
    if (!courseToEdit) {
      return;
    }
    /* Match view-course-page: drawer + chips use DUS-filtered `schools` only (see `filteredSchoolNids`). */
    setCourseToMap({
      id: editCourseId,
      name: courseToEdit.name,
      schools: filteredEditSchoolNids,
    });
  }, [editCourseId, courseToEdit, filteredEditSchoolNids]);

  const handleSaveAndMapToSchools = useCallback(() => {
    if (descriptionPlainTextExceedsMax || prerequisiteTextExceedsMax) {
      return;
    }
    if (!canSaveCourse) {
      return;
    }
    if (!isEditMode && courseIdInUse) {
      return;
    }
    const creditsNum = parseCreditsValue(credits);
    const gradesArray = GRADE_LEVEL_CHECKBOX_OPTIONS.filter(
      (opt) => opt.value !== 'select-all' && gradeLevels[opt.value]
    ).map((opt) => parseInt(opt.value, 10));

    const subjectAreaPayload: SubjectAreaRef | undefined = subjectArea
      ? subjectAreaRefFromSelectValue(subjectArea, subjectAreaList)
      : undefined;

    const payload: { course: Partial<CourseDetail> } = {
      course: {
        name: courseName.trim(),
        id: courseId.trim(),
        stateId: stateId.trim() || undefined,
        subjectArea: subjectAreaPayload,
        credits: creditsNum,
        grades: gradesArray,
        status: status as CourseStatus,
        schools: [],
        description: normalizeDescriptionForSave(courseDescription),
        instructionalLevel: instructionalLevel.trim() || undefined,
        creditType: creditType.trim() || undefined,
        courseDuration: courseDuration.trim() || undefined,
        repeatable: canBeRepeatedForCredit,
        cteCourse: qualifiesAsCTE,
        permissionRequired: requiresTeacherRecommendation,
        prerequisites: (() => {
          const t = stripNewlines(prerequisiteDescription).trim();
          return t || undefined;
        })(),
        stateCore: false,
        elective: false,
        techPrepCourse: false,
        coursePrerequisites: prerequisiteGroupsToPayload(prerequisiteGroups),
        courseCorequisites: corequisiteSelected.length
          ? [corequisiteSelected.map((c) => c.id)]
          : [],
        ...creditRecoveryAndEquivalentsToApiPayload(creditRecoverySelected, equivalentSelected),
        studentGroupRestrictions: [],
      },
    };

    createCourseMutation.mutate(payload, {
      onSuccess: () => {
        showToast('success', {
          heading: formatCourseAddedToCatalogToastHeading(courseName.trim()),
        });
        navigate(`/${STAFF_ROUTES.courseCatalog.view(courseId.trim())}`, {
          state: { openMapping: true },
        });
      },
      onError: () => {
        showToast('error', { heading: 'Unable to create course. Please try again.' });
      },
    });
  }, [
    courseName,
    courseId,
    stateId,
    subjectArea,
    subjectAreaList,
    credits,
    courseDuration,
    gradeLevels,
    status,
    courseDescription,
    instructionalLevel,
    creditType,
    canBeRepeatedForCredit,
    qualifiesAsCTE,
    requiresTeacherRecommendation,
    prerequisiteDescription,
    creditRecoverySelected,
    equivalentSelected,
    prerequisiteGroups,
    corequisiteSelected,
    createCourseMutation,
    showToast,
    navigate,
    isEditMode,
    courseIdInUse,
    canSaveCourse,
    descriptionPlainTextExceedsMax,
    prerequisiteTextExceedsMax,
  ]);

  const handleAddSubjectArea = useCallback(() => {
    setAddSubjectAreaModalState({
      ...INITIAL_ADD_SUBJECT_AREA_MODAL_STATE,
      isOpen: true,
    });
  }, []);

  const handleCloseSubjectAreaModal = useCallback((): void => {
    setAddSubjectAreaModalState(INITIAL_ADD_SUBJECT_AREA_MODAL_STATE);
  }, []);

  const handleSaveSubjectArea = useCallback((): void => {
    const normalizedName = newSubjectAreaName.trim();
    if (!normalizedName || !newSelectedScedCode) {
      return;
    }
    createSubjectArea.mutate(
      {
        subjectArea: {
          name: normalizedName,
          SCEDSubject: Number(newSelectedScedCode),
          locked: false,
        },
      },
      {
        onSuccess: (created) => {
          showToast('success', { heading: `${normalizedName} subject area added` });
          handleCloseSubjectAreaModal();
          updateFormState({
            subjectArea: subjectAreaToOptionValue({
              name: created.name,
              CSSCCourse: created.CSSCCourse ?? 0,
              SCEDSubject: created.SCEDSubject ?? 0,
            }),
          });
        },
        onError: () => {
          showToast('error', { heading: `${newSubjectAreaName} subject area failed to save` });
        },
      }
    );
  }, [
    newSubjectAreaName,
    newSelectedScedCode,
    createSubjectArea,
    showToast,
    handleCloseSubjectAreaModal,
    updateFormState,
  ]);

  const scedOptions = useMemo(
    () => SCEDSubjectCodes.map((item) => ({ text: item.description, value: String(item.code) })),
    []
  );

  const isDuplicateForAdd = useMemo(
    () =>
      isDuplicateSubjectArea(
        newSubjectAreaName,
        Number(newSelectedScedCode),
        subjectAreasData?.data ?? []
      ),
    [newSubjectAreaName, newSelectedScedCode, subjectAreasData?.data]
  );

  const breadcrumbs = useMemo(
    () =>
      getAddCourseBreadcrumbs({
        onBreadcrumbLeave: clearCourseCatalogFilterSession,
        isEditMode,
      }),
    [isEditMode]
  );

  return (
    <>
      <FormPageShell
        dataTestId="add-course-page"
        breadcrumbs={breadcrumbs}
        breadcrumbsId={`${COURSE_CATALOG_ID.Breadcrumbs}-add`}
        pageTitle={isEditMode ? ADD_COURSE_PAGE_TEXT.EditTitle : ADD_COURSE_PAGE_TEXT.Title}
      >
        <div className={styles.addCourseFormPageColumn}>
          <MultiSectionFormLayout
            formTestId="add-course-form"
            footer={{
              cancelLabel: ADD_COURSE_PAGE_TEXT.Cancel,
              saveLabel: ADD_COURSE_PAGE_TEXT.Save,
              onCancel: handleCancel,
              onSave: handleSave,
              saveDisabled:
                createCourseMutation.isPending ||
                updateCourseMutation.isPending ||
                isCourseToEditLoading ||
                !canSaveCourse,
              primaryButton:
                !isEditMode && isDistrictLevel
                  ? {
                      id: 'add-course-save-and-map',
                      text: ADD_COURSE_PAGE_TEXT.SaveAndMapToSchools,
                      onClick: handleSaveAndMapToSchools,
                      disabled:
                        !canSaveCourse ||
                        createCourseMutation.isPending ||
                        updateCourseMutation.isPending,
                    }
                  : undefined,
              tertiaryButton: undefined,
            }}
          >
            <div className={styles.addCourseFormScope}>
              {/* ── Overview ──────────────────────────────────────────────────────── */}
              <div className={styles.addCourseOverviewTab}>
                <h2 className={styles.addCourseSectionTitle}>
                  {ADD_COURSE_PAGE_TEXT.SectionOverview}
                </h2>
                <div className={styles.addCourseOverviewContent}>
                  {isEditMode && isDistrictLevel && (
                    <div className={styles.addCourseMappedSchoolsField}>
                      <span className={styles.addCourseFieldLabel}>
                        {VIEW_COURSE_PAGE_TEXT.MappedSchools}
                      </span>
                      <div className={styles.addCourseMappedSchoolsBlock}>
                        {isCourseToEditLoading ? (
                          <p className={styles.addCourseHelper}>Loading course…</p>
                        ) : !courseToEdit ? (
                          <p className={styles.addCourseHelper}>Unable to load course.</p>
                        ) : mappedEditSchoolsDisplay.length === 0 ? (
                          <div className={styles.addCourseMappedSchoolsWarning} role="status">
                            <NeonIcon
                              dataIcon="status-warning"
                              dataSize="small"
                              className={styles.addCourseMappedSchoolsWarningIcon}
                              aria-hidden="true"
                            />
                            <span className={styles.addCourseMappedSchoolsValue}>
                              {VIEW_COURSE_PAGE_TEXT.NoSchoolsMapped}
                            </span>
                          </div>
                        ) : (
                          <div className={styles.addCourseMappedSchoolsChips}>
                            {mappedEditSchoolsDisplay.map(({ nid, label }) => (
                              <NeonTag
                                key={nid}
                                id={`school-chip-${nid}`}
                                dataText={label}
                                dataType="default"
                              />
                            ))}
                          </div>
                        )}
                        <NeonButton
                          id="edit-course-map-schools-inline"
                          dataType={NeonButtonTypes.Secondary}
                          dataText={`+ ${VIEW_COURSE_PAGE_TEXT.MapCourseToSchools}`}
                          onClick={handleOpenMappingDrawerForEdit}
                          disabled={isCourseToEditLoading}
                        />
                      </div>
                    </div>
                  )}

                  <NeonTextField
                    id="add-course-name"
                    dataLabelText={ADD_COURSE_PAGE_TEXT.CourseName}
                    dataPlaceholderText={`Enter ${ADD_COURSE_PAGE_TEXT.CourseName.toLowerCase()}`}
                    dataIsRequired
                    modelValue={courseName}
                    modelValueChange={(v: string | undefined) =>
                      updateFormState({ courseName: v ?? '' })
                    }
                  />

                  <div className={styles.addCourseRow}>
                    <div className={styles.addCourseIdCell}>
                      <NeonTextField
                        id="add-course-id"
                        dataLabelText={ADD_COURSE_PAGE_TEXT.CourseId}
                        dataPlaceholderText={`Enter ${ADD_COURSE_PAGE_TEXT.CourseId}`}
                        {...(isEditMode ? { dataIsReadOnly: true } : { dataIsRequired: true })}
                        {...(courseIdHelperText !== undefined
                          ? { dataHelperText: courseIdHelperText }
                          : {})}
                        modelValue={courseId}
                        modelValueChange={(v: string | undefined) => {
                          if (!isEditMode) {
                            updateFormState({ courseId: normalizeCourseIdInput(v ?? '') });
                          }
                        }}
                      />
                      {courseIdInUse && !isEditMode ? (
                        <p
                          id="add-course-id-in-use-error"
                          className={styles.addCourseFieldError}
                          role="alert"
                        >
                          {ADD_COURSE_PAGE_TEXT.CourseIdAlreadyInUse}
                        </p>
                      ) : null}
                    </div>
                    <NeonTextField
                      id="add-course-state-id"
                      dataLabelText={ADD_COURSE_PAGE_TEXT.StateId}
                      dataPlaceholderText={`Enter ${ADD_COURSE_PAGE_TEXT.StateId}`}
                      modelValue={stateId}
                      modelValueChange={(v: string | undefined) =>
                        updateFormState({ stateId: v ?? '' })
                      }
                    />
                  </div>

                  <div className={`${styles.addCourseRow} ${styles.addCourseRowSubject}`}>
                    <NeonSelectField
                      id="add-course-subject-area"
                      dataLabelText={ADD_COURSE_PAGE_TEXT.SubjectArea}
                      dataPlaceholderText={`Select ${ADD_COURSE_PAGE_TEXT.SubjectArea.toLowerCase()}`}
                      dataIsRequired
                      options={subjectAreaOptions}
                      modelValue={subjectArea || undefined}
                      modelValueChange={(v: string | undefined) =>
                        updateFormState({ subjectArea: v ?? '' })
                      }
                    />
                    <NeonButton
                      id="add-course-add-subject-area"
                      dataType={NeonButtonTypes.Secondary}
                      dataText={ADD_COURSE_PAGE_TEXT.AddSubjectArea}
                      dataIcon="add"
                      dataIconPosition="left"
                      onClick={handleAddSubjectArea}
                    />
                  </div>

                  <div className={`${styles.addCourseRow} ${styles.addCourseCreditsDurationRow}`}>
                    <div className={styles.addCourseCreditsCell}>
                      <div className={styles.addCourseCreditsField}>
                        <NeonNumberField
                          id="add-course-credits"
                          dataLabelText={ADD_COURSE_PAGE_TEXT.Credits}
                          dataShowLabel={true}
                          dataIsRequired
                          dataHelperText={ADD_COURSE_PAGE_TEXT.CreditsHelper}
                          modelValue={credits}
                          dataMaxDecimals={DEFAULT_CREDITS_INPUT_MAX_DECIMALS}
                          modelValueChange={(v: number | undefined) => {
                            updateFormState({ credits: v });
                          }}
                          dataSize="small"
                        />
                      </div>
                    </div>
                    <NeonSelectField
                      key={`add-course-duration-${courseDurationSelectRemountKey}`}
                      id="add-course-duration"
                      dataLabelText={ADD_COURSE_PAGE_TEXT.CourseDuration}
                      dataPlaceholderText={ADD_COURSE_PAGE_TEXT.CourseDurationPlaceholder}
                      options={courseDurationOptions}
                      modelValue={courseDuration.trim() === '' ? undefined : courseDuration}
                      modelValueChange={(v: string | undefined) => {
                        if (v === undefined || v === COURSE_DURATION_OPTION_CLEAR) {
                          updateFormState({ courseDuration: '' });
                          if (v === COURSE_DURATION_OPTION_CLEAR) {
                            setCourseDurationSelectRemountKey((k) => k + 1);
                          }
                        } else {
                          updateFormState({ courseDuration: v });
                        }
                      }}
                    />
                  </div>

                  <div
                    className={`${styles.addCourseFieldGroup} ${styles.addCourseGradeLevelFieldGroup}`}
                  >
                    <div className={styles.addCourseCheckboxGroup}>
                      <NeonCheckboxField
                        id="add-course-grade-levels"
                        dataLabelText={ADD_COURSE_PAGE_TEXT.GradeLevels}
                        dataShowLabel={true}
                        options={gradeLevelCheckboxOptions}
                        modelValue={selectedGradeLevelValues}
                        dataIsRequired
                        dataControlLayout={'row'}
                        modelValueChange={(selectedValues) => {
                          setFormState((prev) => ({
                            ...prev,
                            gradeLevels: gradeLevelsFromSelection(selectedValues, prev.gradeLevels),
                          }));
                        }}
                      />
                    </div>
                  </div>

                  <NeonSelectField
                    id="add-course-status"
                    dataLabelText={ADD_COURSE_PAGE_TEXT.Status}
                    options={statusOptions}
                    modelValue={status}
                    modelValueChange={(v: string | undefined) =>
                      v && updateFormState({ status: v })
                    }
                  />
                </div>
              </div>

              {/* ── Details — Figma spacing: 16px stack (node 7411:13080) ───────────── */}
              <div className={styles.addCourseDetailsSection}>
                <div className={styles.addCourseDivider} />
                <h2 className={styles.addCourseSectionTitle}>
                  {ADD_COURSE_PAGE_TEXT.SectionDetails}
                </h2>

                <div className={styles.addCourseRichText}>
                  <DescriptionField
                    id="add-course-description"
                    label={ADD_COURSE_PAGE_TEXT.CourseDescription}
                    value={courseDescription}
                    onChange={(v) => updateFormState({ courseDescription: v })}
                  />
                  {descriptionPlainTextExceedsMax ? (
                    <p
                      id="add-course-description-error"
                      className={styles.addCourseFieldError}
                      role="alert"
                    >
                      {ADD_COURSE_PAGE_TEXT.CourseDescriptionTooLong}
                    </p>
                  ) : null}
                </div>

                <div className={styles.addCourseInstructionalCreditStack}>
                  <NeonSelectField
                    key={`add-course-instructional-level-${instructionalLevelSelectRemountKey}`}
                    id="add-course-instructional-level"
                    dataLabelText={ADD_COURSE_PAGE_TEXT.InstructionalLevel}
                    dataPlaceholderText={ADD_COURSE_PAGE_TEXT.InstructionalLevelPlaceholder}
                    options={instructionalLevelOptions}
                    modelValue={instructionalLevel.trim() === '' ? undefined : instructionalLevel}
                    modelValueChange={(v: string | undefined) => {
                      if (v === undefined || v === INSTRUCTIONAL_LEVEL_OPTION_CLEAR) {
                        updateFormState({ instructionalLevel: '' });
                        if (v === INSTRUCTIONAL_LEVEL_OPTION_CLEAR) {
                          setInstructionalLevelSelectRemountKey((k) => k + 1);
                        }
                      } else {
                        updateFormState({ instructionalLevel: v });
                      }
                    }}
                  />

                  <NeonTextField
                    id="add-course-credit-type"
                    dataLabelText={ADD_COURSE_PAGE_TEXT.CreditType}
                    dataPlaceholderText=""
                    modelValue={creditType}
                    modelValueChange={(v: string | undefined) =>
                      updateFormState({ creditType: v ?? '' })
                    }
                  />
                </div>

                <div className={styles.addCourseFieldGroup}>
                  <span className={styles.addCourseFieldLabel}>
                    {ADD_COURSE_PAGE_TEXT.CourseAttributes}
                  </span>
                  <div
                    className={`${styles.addCourseCheckboxGroup} ${styles.addCourseCheckboxGroupVertical}`}
                  >
                    <NeonCheckboxSingle
                      id="add-course-can-be-repeated"
                      dataLabelText={ADD_COURSE_PAGE_TEXT.CanBeRepeatedForCredit}
                      value={canBeRepeatedForCredit}
                      onInput={(e: Event) => {
                        const t = e.target as HTMLInputElement & { getValue?: () => boolean };
                        updateFormState({
                          canBeRepeatedForCredit: t?.getValue?.() ?? t?.checked ?? false,
                        });
                      }}
                    />
                    <NeonCheckboxSingle
                      id="add-course-cte"
                      dataLabelText={ADD_COURSE_PAGE_TEXT.QualifiesAsCTECourse}
                      value={qualifiesAsCTE}
                      onInput={(e: Event) => {
                        const t = e.target as HTMLInputElement & { getValue?: () => boolean };
                        updateFormState({ qualifiesAsCTE: t?.getValue?.() ?? t?.checked ?? false });
                      }}
                    />
                    <NeonCheckboxSingle
                      id="add-course-requires-teacher-recommendation"
                      dataLabelText={ADD_COURSE_PAGE_TEXT.RequiresTeacherRecommendation}
                      value={requiresTeacherRecommendation}
                      onInput={(e: Event) => {
                        const t = e.target as HTMLInputElement & { getValue?: () => boolean };
                        updateFormState({
                          requiresTeacherRecommendation: t?.getValue?.() ?? t?.checked ?? false,
                        });
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* ── Course Relationships — same 16px stack as Figma ───────────────── */}
              <div className={styles.addCourseRelationshipsSection}>
                <div className={styles.addCourseDivider} />
                <h2 className={styles.addCourseSectionTitle}>
                  {ADD_COURSE_PAGE_TEXT.SectionCourseRelationships}
                </h2>

                {showCreditRecoverySection && (
                  <>
                    <AddCourseCatalogMultiSelect
                      id="add-course-credit-recovery-multiselect"
                      fieldLabelText={ADD_COURSE_PAGE_TEXT.CreditRecovery}
                      helperText={ADD_COURSE_PAGE_TEXT.CreditRecoveryHelper}
                      helperId="add-course-credit-recovery-helper"
                      placeholderText={ADD_COURSE_PAGE_TEXT.SearchByCourseNameOrId}
                      selected={creditRecoverySelected}
                      onSelectedChange={onCreditRecoverySelectionChange}
                      optionIdPrefix="cr"
                      enabledFetch={showCreditRecoverySection}
                    />
                    <AddCourseCatalogMultiSelect
                      id="add-course-equivalent-multiselect"
                      fieldLabelText={ADD_COURSE_PAGE_TEXT.EquivalentCourses}
                      helperText={ADD_COURSE_PAGE_TEXT.EquivalentHelper}
                      helperId="add-course-equivalent-helper"
                      placeholderText={ADD_COURSE_PAGE_TEXT.SearchByCourseNameOrId}
                      selected={equivalentSelected}
                      onSelectedChange={onEquivalentSelectionChange}
                      optionIdPrefix="eq"
                      enabledFetch={showCreditRecoverySection}
                    />
                  </>
                )}

                <div className={styles.addCoursePrerequisiteSection}>
                  {prerequisiteGroups.map((group, groupIndex) => (
                    <div
                      key={`prereq-group-${groupIndex}`}
                      className={styles.addCoursePrerequisiteGroup}
                    >
                      {groupIndex > 0 ? (
                        <>
                          <div
                            className={styles.addCoursePrerequisiteOrBar}
                            aria-hidden={true}
                            role="presentation"
                          >
                            <span className={styles.addCoursePrerequisiteOrBarLabel}>
                              {ADD_COURSE_PAGE_TEXT.PrerequisiteOrBetweenGroups}
                            </span>
                          </div>
                          <div className={styles.addCoursePrerequisiteGroupRow}>
                            <div className={styles.addCoursePrerequisiteGroupMain}>
                              <AddCourseCatalogMultiSelect
                                id={`add-course-prerequisite-multiselect-${groupIndex}`}
                                fieldLabelText={
                                  ADD_COURSE_PAGE_TEXT.PrerequisiteAlternativeSetLabel
                                }
                                placeholderText={ADD_COURSE_PAGE_TEXT.SearchByCourseNameOrId}
                                selected={group}
                                onSelectedChange={(next) =>
                                  onPrerequisiteGroupChange(groupIndex, next)
                                }
                                optionIdPrefix={`pr-${groupIndex}`}
                                hideOuterCaption
                              />
                            </div>
                            <div className={styles.addCoursePrerequisiteDeleteWrap}>
                              <NeonButton
                                id={`add-course-remove-or-condition-${groupIndex}`}
                                dataType={NeonButtonTypes.Utility}
                                dataIcon="delete"
                                dataText=""
                                aria-label={ADD_COURSE_PAGE_TEXT.RemoveOrConditionAriaLabel}
                                dataTooltipText={ADD_COURSE_PAGE_TEXT.RemoveOrConditionTooltip}
                                onClick={() => removePrerequisiteOrGroup(groupIndex)}
                              />
                            </div>
                          </div>
                        </>
                      ) : (
                        <AddCourseCatalogMultiSelect
                          id={`add-course-prerequisite-multiselect-${groupIndex}`}
                          fieldLabelText={ADD_COURSE_PAGE_TEXT.CoursePrerequisites}
                          helperText={ADD_COURSE_PAGE_TEXT.PrerequisiteHelper}
                          helperId="add-course-prerequisite-helper"
                          placeholderText={ADD_COURSE_PAGE_TEXT.SearchByCourseNameOrId}
                          selected={group}
                          onSelectedChange={(next) => onPrerequisiteGroupChange(groupIndex, next)}
                          optionIdPrefix={`pr-${groupIndex}`}
                        />
                      )}
                    </div>
                  ))}
                  <NeonButton
                    id="add-course-add-or-condition"
                    dataType={NeonButtonTypes.Borderless}
                    dataText={ADD_COURSE_PAGE_TEXT.AddOrCondition}
                    dataIcon="add"
                    dataIconPosition="left"
                    onClick={addPrerequisiteOrGroup}
                  />
                </div>

                <div className={styles.addCourseFieldGroup}>
                  <label className={styles.addCourseFieldLabel} htmlFor="add-course-prereq-desc">
                    {ADD_COURSE_PAGE_TEXT.PrerequisiteText}
                  </label>
                  <p id="add-course-prereq-desc-helper" className={styles.addCourseHelper}>
                    {ADD_COURSE_PAGE_TEXT.PrerequisiteTextHelper}
                  </p>
                  <textarea
                    id="add-course-prereq-desc"
                    className={styles.addCourseTextarea}
                    rows={4}
                    value={prerequisiteDescription}
                    onChange={(e) =>
                      updateFormState({ prerequisiteDescription: stripNewlines(e.target.value) })
                    }
                    aria-describedby={`add-course-prereq-desc-helper${prerequisiteTextExceedsMax ? ' add-course-prereq-desc-error' : ''} add-course-prereq-desc-charcount`}
                  />
                  <p id="add-course-prereq-desc-charcount" className={styles.addCourseCharCount}>
                    {`Characters: ${prerequisiteDescription.length}/${MAX_PREREQUISITE_DESCRIPTION_LENGTH}`}
                  </p>
                  {prerequisiteTextExceedsMax ? (
                    <p
                      id="add-course-prereq-desc-error"
                      className={styles.addCourseFieldError}
                      role="alert"
                    >
                      {ADD_COURSE_PAGE_TEXT.PrerequisiteDescriptionTooLong}
                    </p>
                  ) : null}
                </div>

                <AddCourseCatalogMultiSelect
                  id="add-course-corequisite-multiselect"
                  fieldLabelText={ADD_COURSE_PAGE_TEXT.CourseCorequisites}
                  placeholderText={ADD_COURSE_PAGE_TEXT.SearchByCourseNameOrId}
                  selected={corequisiteSelected}
                  onSelectedChange={onCorequisiteSelectionChange}
                  optionIdPrefix="co"
                />
              </div>
            </div>
          </MultiSectionFormLayout>
        </div>
      </FormPageShell>
      <AddSubjectAreaDialog
        isOpen={showAddSubjectAreaModal}
        subjectAreaName={newSubjectAreaName}
        selectedScedCode={newSelectedScedCode}
        scedOptions={scedOptions}
        isSaving={createSubjectArea.isPending}
        isDuplicate={isDuplicateForAdd}
        onSubjectAreaNameChange={(value) =>
          setAddSubjectAreaModalState((prev) => ({ ...prev, subjectAreaName: value }))
        }
        onSelectedScedCodeChange={(value) =>
          setAddSubjectAreaModalState((prev) => ({ ...prev, selectedScedCode: value }))
        }
        onSave={handleSaveSubjectArea}
        onClose={handleCloseSubjectAreaModal}
      />
      {courseToMap && (
        <CourseSchoolsMappingDrawer
          courseId={courseToMap.id}
          courseName={courseToMap.name}
          currentSchoolNids={(courseToMap.schools ?? []).filter(
            (nid) => !DUS_NID_PATTERN.test(nid)
          )}
          allSchools={schoolsData ?? []}
          isSchoolsLoading={isSchoolsLoading}
          /* View course always opens manual selection first (`view-course-page.tsx`). */
          defaultRadio={isEditMode ? 'select' : 'copy'}
          onClose={() => setCourseToMap(null)}
          onSaveSuccess={() => {
            if (!isEditMode) {
              navigate(`/${STAFF_ROUTES.courseCatalog.list}`);
            } else {
              void refetchCourseToEdit();
            }
          }}
        />
      )}
    </>
  );
}
